import {
  SoloConfig,
  SoloPhrase,
  SoloOutput,
  SoloInstrument,
  SOLO_INSTRUMENT_LABELS
} from '@/types/soloChoreographer';
import { getTechniqueById } from '../data/techniques';

// ═══════════════════════════════════════════════════════════════
// TEXT CONVERTERS
// ═══════════════════════════════════════════════════════════════

// Trimmed 2026-09-12 (verified: a hand-tightened version of these exact
// phrases, run through a real generation, preserved the same register/
// intensity signal in the delivered lyrics as the padded originals). Each
// tier keeps its one distinguishing word; drops the redundant restatement
// that added nothing an LLM/Suno wasn't already getting from the word
// itself ("confident and present" -> "confident", "the sweet spot" carried
// no acoustic information at all -> dropped).
function intensityToText(intensity: number): string {
  if (intensity <= 15) return 'whisper quiet';
  if (intensity <= 30) return 'soft, restrained';
  if (intensity <= 45) return 'building presence';
  if (intensity <= 60) return 'confident';
  if (intensity <= 75) return 'powerful, driving';
  if (intensity <= 90) return 'intense, pushing hard';
  return 'explosive';
}

function registerToText(register: string): string {
  switch (register) {
    case 'low': return 'low register, growling';
    case 'mid': return 'mid register';
    case 'high': return 'high register, soaring';
    case 'climb': return 'climbing through the registers';
    case 'descend': return 'descending';
    default: return 'mid register';
  }
}

function vibratoToText(vibrato: string): string | null {
  switch (vibrato) {
    case 'subtle': return 'subtle vibrato';
    case 'slow_wide': return 'slow wide vibrato';
    case 'fast_tight': return 'fast tight vibrato';
    case 'aggressive': return 'aggressive vibrato shake';
    default: return null;
  }
}

function effectsToText(effects: string): string | null {
  switch (effects) {
    case 'delay_trails': return 'delay trails echoing behind';
    case 'reverb_wash': return 'reverb wash, spacious and wide';
    case 'feedback_bloom': return 'feedback beginning to bloom';
    case 'volume_swell': return 'volume swell breathing';
    default: return null;
  }
}

function wahToText(wah: string): string | null {
  switch (wah) {
    case 'half_cocked': return 'wah half-cocked for midrange honk';
    case 'slow_sweep': return 'wah pedal sweeping slowly';
    case 'fast_sweep': return 'wah pedal working fast';
    case 'wacka_rhythm': return 'rhythmic wah wacka';
    default: return null;
  }
}

function bandToText(band: string): string | null {
  switch (band) {
    case 'half_time': return 'band drops to half-time feel';
    case 'drops_out': return 'band drops out completely';
    case 'drums_only': return 'drums only underneath';
    case 'bass_and_drums': return 'bass and drums only';
    case 'builds_with_solo': return 'band builds intensity with the solo';
    default: return null;
  }
}

function instrumentToSectionHeader(instrument: SoloInstrument): string {
  switch (instrument) {
    case 'lead_guitar': return '[Guitar Solo]';
    case 'slide_guitar': return '[Slide Guitar Solo]';
    case 'keys': return '[Keys Solo]';
    case 'harmonica': return '[Harmonica Solo]';
    case 'saxophone': return '[Saxophone Solo]';
    case 'pedal_steel': return '[Pedal Steel Solo]';
    case 'organ': return '[Organ Solo]';
    default: return '[Solo]';
  }
}

// ═══════════════════════════════════════════════════════════════
// PHRASE OUTPUT
// ═══════════════════════════════════════════════════════════════

function generatePhraseOutput(phrase: SoloPhrase): string {
  const parts: string[] = [];
  
  // Technique
  if (phrase.techniqueId) {
    const technique = getTechniqueById(phrase.techniqueId);
    if (technique) {
      parts.push(technique.outputPhrase);
    }
  }
  
  // Register
  parts.push(registerToText(phrase.register));
  
  // Intensity
  parts.push(intensityToText(phrase.intensity));
  
  // Optional additions
  const vibrato = vibratoToText(phrase.vibrato);
  if (vibrato) parts.push(vibrato);
  
  const effects = effectsToText(phrase.effects);
  if (effects) parts.push(effects);
  
  const wah = wahToText(phrase.wah);
  if (wah) parts.push(wah);
  
  const band = bandToText(phrase.bandInstruction);
  if (band) parts.push(band);
  
  // Custom description
  if (phrase.customDescription.trim()) {
    parts.push(phrase.customDescription.trim());
  }
  
  return parts.join(', ') + '.';
}

// ═══════════════════════════════════════════════════════════════
// MAIN OUTPUT GENERATOR
// ═══════════════════════════════════════════════════════════════

export function generateSoloOutput(config: SoloConfig): SoloOutput {
  if (!config.enabled || config.phrases.length === 0) {
    return { 
      layerBlock: '', 
      compactSummary: '',
      sectionHeader: ''
    };
  }
  
  const lines: string[] = [];
  const sectionHeader = instrumentToSectionHeader(config.instrument);
  
  // Header
  lines.push('{LAYER-6 SOLO CHOREOGRAPHY}');
  lines.push(`[Solo_Instrument: ${config.instrument} | Solo_Length: ${config.length}_bars]`);
  lines.push('');
  
  // Each phrase
  config.phrases.forEach(phrase => {
    lines.push(`[Bar ${phrase.barRange}: ${phrase.name.toUpperCase()}]`);
    lines.push(`(${generatePhraseOutput(phrase)})`);
    lines.push('');
  });
  
  // Compact summary
  const techniqueNames = config.phrases
    .filter(p => p.techniqueId)
    .map(p => {
      const tech = getTechniqueById(p.techniqueId!);
      return tech?.name || '';
    })
    .filter(Boolean)
    .join(' → ');
  
  const instrumentLabel = SOLO_INSTRUMENT_LABELS[config.instrument];
  const compactSummary = `${config.length}-bar ${instrumentLabel} solo: ${techniqueNames || 'Custom'}`;
  
  return {
    layerBlock: lines.join('\n').trim(),
    compactSummary,
    sectionHeader
  };
}

// ═══════════════════════════════════════════════════════════════
// LYRICS INJECTION
// ═══════════════════════════════════════════════════════════════

/**
 * Replace generic [Guitar Solo] or [Solo] sections in lyrics
 * with the choreographed version
 * 
 * IMPORTANT: Checks if choreography already exists to prevent duplicates
 */
export function injectSoloIntoLyrics(lyrics: string, soloOutput: SoloOutput): string {
  if (!soloOutput.layerBlock) return lyrics;
  
  // CHECK: If choreography is already present, don't inject again
  if (lyrics.includes('{LAYER-6 SOLO CHOREOGRAPHY}')) {
    return lyrics;
  }

  // Patterns to match various solo section headers WITH their AI-generated content
  // This captures the header AND everything until the next section
  const soloPatterns = [
    /\[Guitar Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Slide Guitar Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Keys Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Keyboard Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Piano Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Organ Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Harmonica Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Saxophone Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Sax Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Pedal Steel Solo\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Instrumental\][\s\S]*?(?=\n\n?\[(?!Bar))/gi,
    /\[Instrumental Break\][\s\S]*?(?=\n\n?\[(?!Bar))/gi
  ];
  
  // Build replacement text
  const replacement = `${soloOutput.sectionHeader}\n${soloOutput.layerBlock}\n`;
  
  let modifiedLyrics = lyrics;
  
  // Try each pattern until we find a match
  for (const pattern of soloPatterns) {
    if (pattern.test(modifiedLyrics)) {
      // Reset lastIndex for global regex
      pattern.lastIndex = 0;
      modifiedLyrics = modifiedLyrics.replace(pattern, replacement);
      break;
    }
  }
  
  return modifiedLyrics;
}

/**
 * Check if lyrics contain a solo section
 */
export function lyricsHaveSoloSection(lyrics: string): boolean {
  const soloPatterns = [
    /\[Guitar Solo\]/i,
    /\[Solo\]/i,
    /\[Slide Guitar Solo\]/i,
    /\[Keys Solo\]/i,
    /\[Keyboard Solo\]/i,
    /\[Piano Solo\]/i,
    /\[Organ Solo\]/i,
    /\[Harmonica Solo\]/i,
    /\[Saxophone Solo\]/i,
    /\[Sax Solo\]/i,
    /\[Pedal Steel Solo\]/i,
    /\[Instrumental\]/i,
    /\[Instrumental Break\]/i
  ];
  
  return soloPatterns.some(pattern => pattern.test(lyrics));
}

/**
 * Check if lyrics already have choreographed solo (not just AI solo)
 */
export function lyricsHaveChoreographedSolo(lyrics: string): boolean {
  return lyrics.includes('{LAYER-6 SOLO CHOREOGRAPHY}');
}
